import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import i18n from '../i18n'
import { api, sessionStorageKeys } from '../api'
import type { Language, Role, User } from '../types'

function readUser(): User | null {
  try {
    const raw = localStorage.getItem(sessionStorageKeys.user)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(readUser())
  const token = ref(localStorage.getItem(sessionStorageKeys.token))
  const initialized = ref(false)
  const isAuthenticated = computed(() => Boolean(user.value && token.value))
  const role = computed<Role | null>(() => user.value?.role ?? null)

  function persist() {
    if (user.value) localStorage.setItem(sessionStorageKeys.user, JSON.stringify(user.value))
    else localStorage.removeItem(sessionStorageKeys.user)
    if (token.value) localStorage.setItem(sessionStorageKeys.token, token.value)
    else localStorage.removeItem(sessionStorageKeys.token)
  }

  function setLocale(language: Language) {
    i18n.global.locale.value = language
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : language === 'pt' ? 'pt-BR' : 'en'
    localStorage.setItem('locale', language)
  }

  async function initialize() {
    if (initialized.value) return
    if (token.value) {
      try {
        const response = await api.me()
        user.value = response.user
        setLocale(response.user.language || 'zh')
      } catch {
        clear()
      }
    }
    initialized.value = true
    persist()
  }

  async function login(username: string, password: string) {
    const response = await api.login(username, password)
    token.value = response.token
    user.value = response.user
    persist()
    setLocale(response.user.language || 'zh')
  }

  function clear() {
    user.value = null
    token.value = null
    persist()
  }

  window.addEventListener('session-expired', clear)

  async function changeLanguage(language: Language) {
    setLocale(language)
    if (user.value) {
      user.value.language = language
      persist()
      await api.updateLanguage(language)
    }
  }

  function replaceUser(next: User) {
    user.value = next
    persist()
  }

  return { user, token, initialized, isAuthenticated, role, initialize, login, clear, changeLanguage, replaceUser }
})
